import { Box } from '@chakra-ui/react';
import { useGetEsrsAssessmentShortDetailsQuery } from 'models';
import React, { RefObject, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'utils/translation';
import { EsrsReportStandardView } from './Report.types';
import { RenderOnViewportEntry } from './ReportUtils';
import ReportContentContainer from './ReportUIComponents/ReportContentContainer';
import { MetricsTableData } from '../DisclosureRequirements';
import EditableDrCard from './ReportComponents/EditableDrCard';
import { Frameworks } from '../Assessment';

export const ReportStandardPage = ({
  standardIndex,
  setStandardIndex,
  standards,
  reportRef,
}: {
  standardIndex: number;
  setStandardIndex: (val: number) => void;
  standards?: EsrsReportStandardView[];
  reportRef: RefObject<HTMLDivElement | null>;
}) => {
  const { esrsAssessmentId = '', framework } = useParams();
  const typedFramework = framework as keyof typeof Frameworks;
  const [searchParams] = useSearchParams();
  const urlDrRef = useMemo(() => searchParams.get('drRef'), [searchParams]);
  const urlMetricRef = useMemo(() => searchParams.get('metricRef'), [searchParams]);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const handleScroll = () => {
    setShowBackToTop((reportRef.current?.scrollTop ?? 0) > 0);
  };
  const [rowData, setRowData] = useState<MetricsTableData>();

  const { data: assessmentData, loading: assessmentLoading } =
    useGetEsrsAssessmentShortDetailsQuery({
      variables: { esrsAssessmentId },
      skip: !esrsAssessmentId || standardIndex !== 0,
    });

  // Appendix, to be discussed
  // const { data: materialityData, loading: materialityLoading } = useGetSpecificMaterialityQuery({
  //   variables: {
  //     esrsAssessmentId,
  //     standardRefs: appedixStandardRefs,
  //     metricRefs: appedixMetricRefs,
  //   },
  //   skip: !esrsAssessmentId,
  // });

  const selectedStandard = useMemo(() => standards?.[standardIndex], [standards, standardIndex]);
  const frameworkName = Frameworks[typedFramework];

  const reportTitle = useMemo(
    () =>
      `${
        frameworkName +
        ' sustainability statement for ' +
        assessmentData?.assessment?.company.name +
        ' ' +
        assessmentData?.assessment?.reportingYear
      }`,
    [assessmentData]
  );

  useEffect(() => {
    reportRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [standardIndex]);

  useEffect(() => {
    if (urlDrRef) {
      const element = document.getElementById(urlDrRef);
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [urlDrRef, standardIndex, standards]);

  return (
    <ReportContentContainer
      handleScroll={handleScroll}
      reportTitle={reportTitle}
      reportRef={reportRef}
      showBackToTop={showBackToTop}
      standardIndex={standardIndex}
      setStandardIndex={setStandardIndex}
      standards={standards}
      loading={assessmentLoading}
      selectedStandard={selectedStandard}
    >
      {selectedStandard?.disclosureRequirementRefs.map((dr) => (
        <Box id={dr.key} w="100%" key={dr.key}>
          <RenderOnViewportEntry threshold={0} key={dr.key} rootMargin="300px 0px 300px 0px">
            <EditableDrCard
              key={`${dr.key}-${standardIndex}`}
              dr={dr}
              standard={selectedStandard}
              rowData={rowData}
              setRowData={setRowData}
              urlMetricRef={urlMetricRef ?? ''}
            />
          </RenderOnViewportEntry>
        </Box>
      ))}
    </ReportContentContainer>
  );
};
